import wixWidget from 'wix-widget';
import getSymbolFromCurrency from 'currency-symbol-map';
import { suffixCurrencies } from 'public/Utilities/misc';
import store from 'store';
import _ from 'lodash';

export async function checkPanelInfoText() {
    // Hide and Show Panel Elements
    let shippingLimits = store.get('shippingLimits');
    let cartDetails = store.get('cartDetails');

    let currency = cartDetails?.currency ?? 'USD';
    const widgetStatus = shippingLimits?.active ? true : false;
    if (widgetStatus) {
        $w('#panelDiscountInfoText').text = `Enjoy free shipping on orders over ${formatPrice(currency, shippingLimits?.limit ?? 1000)}! To update the free shipping threshold, open the Dashboard.`
    } else {
        $w('#panelDiscountInfoText').text = "Enjoy free shipping on orders over $1000! To update the free shipping threshold, open the Dashboard."
    }
}

/**
 * @function
 * @description Updates the value of a Wix Widget property.
 * @param {string} propName - The name of the Wix Widget property.
 * @param {string | number | array | object} data - This can be event output or a direct value.
 * @param {string} updateType - Type of data receiving ('direct', 'event', 'checked'). Default is 'event'.
 * @returns nothing 
 */
export async function setWidgetProp(propName, data, updateType = 'event') {
    let propValue = null;

    switch (updateType) {
    case 'direct':
        propValue = data
        break;
    case 'event':
        propValue = data.target.value;
        break;
    case 'checked':
        propValue = data.target.checked;
        break;

    default:
        propValue = data
        break;
    }

    // Update the local store with the new property value.
    updateLocalStorePropKeyValue(propName, propValue);

    // Set the property value in the Wix Widget.
    await wixWidget.setProps({
        [propName]: propValue
    });
}

/**
 * @function
 * @description Updates a key-value pair in the local store under the 'props' namespace.
 * @param {string} propName - The name of the property (key) to be updated.
 * @param {any} propValue - The new value to be assigned to the property.
 * @returns {void}
 */
function updateLocalStorePropKeyValue(propName, propValue) {
    try {
        // Retrieve existing 'props' from the local store, or initialize an empty object if not present.
        let localProps = store.get('props') || {};

        // Update or add the specified key-value pair.
        localProps[propName] = propValue;

        // Save the updated 'props' object back to the local store.
        store.set('props', localProps);
    } catch (error) {
        // Handle and log any errors that may occur during the update process.
        console.error(`Error updating local store property: ${error.message}`);
    }
}

/**
 * @function
 * @description Controls the visibility and state of a Wix element.
 * @param {string} elementId - The ID of the Wix element.
 * @param {string} visibilityStatus - The desired visibility status ('show', 'hide', 'scroll', 'disable', 'enable').
 * @returns nothing 
 */
export async function controllElementVisibility(elementId, visibilityStatus) {
    try {
        if (Array.isArray(elementId)) {
            for (const id of elementId) {
                await applyVisibility(id, visibilityStatus);
            }
        } else {
            await applyVisibility(elementId, visibilityStatus);
        }
    } catch (error) {
        console.error("Error controlling element visibility:", error);
    }
}

export async function applyVisibility(elementId, visibilityStatus) {
    try {
        switch (visibilityStatus) {
        case 'show':
            await $w(elementId).show();
            await $w(elementId).expand();
            break;
        case 'hide':
            await $w(elementId).hide();
            await $w(elementId).collapse();
            break;
        case 'scroll':
            await $w(elementId).scrollTo();
            break;
        case 'disable':
            await $w(elementId).disable();
            break;
        case 'enable':
            await $w(elementId).enable();
            break;
        default:
            await $w(elementId).show();
            await $w(elementId).expand();
            break;
        }
    } catch (error) {
        console.error("Error applying visibility:", error);
    }
}

export function formatPrice(currency, price) {
    // If the price is not a number or is null, return a default value
    if (typeof price !== 'number' || price === null) {
        console.log('Invalid price provided:', price);
        return; // Or any default value you prefer
    }
    const symbol = getSymbolFromCurrency(currency);

    // Convert to string with or without decimals depending on the value
    const hasDecimals = price % 1 !== 0;
    const formattedPrice = hasDecimals ? price.toFixed(2) : price.toFixed(0);

    if (suffixCurrencies.includes(currency)) {
        return `${formattedPrice} ${symbol}`;
    } else {
        return `${symbol}${formattedPrice}`;
    }
}

/**
 * Creates a debounced updater function for a specific element and property.
 * @param {string} elementId - The ID of the element to control.
 * @param {string} propName - The property name to update.
 * @param {number} [debounceDelay=500] - The debounce delay in milliseconds.
 * @param {number} [updateWait=1500] - The wait time after updating in milliseconds.
 * @returns {function} - A debounced function to update the element.
 */
export function createDebouncedUpdater(elementId, propName, debounceDelay = 500, updateWait = 1500, type = 'directWidgetProp') {
    let lastValue = 0;

    const updateValueDebounced = _.debounce(async () => {
        if (type == 'directWidgetProp') {
            await controllElementVisibility(elementId, 'disable');
            await setWidgetProp(propName, lastValue, 'direct');
            await new Promise(resolve => setTimeout(resolve, updateWait));
            await controllElementVisibility(elementId, 'enable');
        }
    }, debounceDelay);

    return (value) => {
        lastValue = value;
        updateValueDebounced();
    };
}